import React, {useContext, useEffect, useState, useMemo} from 'react';
import _ from 'lodash';
import {JwtContext, refreshToken} from './ducks/auth';
import {HubConnectionBuilder} from '@microsoft/signalr';
import toast from 'react-hot-toast';
import {useTranslation} from 'react-i18next';

const SignalRContext = React.createContext(null);

function WithSignalR({children}) {
    let jwt = useContext(JwtContext);
    if (jwt == null) {
        // we can't connect to SignalR if the bearer token isn't available, so the context is null
        return <SignalRContext.Provider value={null}>{children}</SignalRContext.Provider>;
    } else {
        // user is logged in, actually connect to SignalR
        return <SignalRConnection>{children}</SignalRConnection>;
    }
}

const RECONNECT_DELAYS = [0, 2000, 2000, 2000, 2000, 2000, 5000, 5000, 5000, 5000, 5000, 30000];
function SignalRConnection({children}) {
    let [hubConnection, setHubConnection] = useState(null);
    let [hubConnectionState, setHubConnectionState] = useState(null);
    const {t} = useTranslation();

    useEffect(() => {
        let connection = new HubConnectionBuilder()
            .withUrl('/api/live', {
                accessTokenFactory: async () => {
                    const refreshResponse = await refreshToken();
                    return refreshResponse?.token;
                },
            })
            .withAutomaticReconnect(RECONNECT_DELAYS)
            .build();

        connection
            .start()
            .then(() => setHubConnection(connection))
            .catch(() => {
                // This error is caught when connecting to the Azure SignalR service fails.
                // Normally, this is because we are in development mode and the SignalR service has reached the free tier limit.
                // It could also mean that SignalR is actually down due to an outage.
                toast(t('signalRStatus.failedToConnect'), {
                    duration: Infinity,
                    position: 'bottom-center',
                    id: 'connectionStatus',
                    className: 'danger',
                });
            });

        connection.onclose((err) => setHubConnectionState(err));
        connection.onreconnected((err) => setHubConnectionState(err));
        connection.onreconnecting((err) => setHubConnectionState(err));
        return () => connection.stop();
    }, []);

    const context = useMemo(
        () =>
            !hubConnection
                ? null
                : {
                      on: _.bind(hubConnection.on, hubConnection),
                      off: _.bind(hubConnection.off, hubConnection),
                      invoke: _.bind(hubConnection.invoke, hubConnection),
                      connectionState: hubConnection.connectionState,
                  },
        [
            hubConnection?.on,
            hubConnection?.off,
            hubConnection?.invoke,
            hubConnection?.connectionState,
            hubConnectionState,
        ],
    );

    return <SignalRContext.Provider value={context}>{children}</SignalRContext.Provider>;
}

export {WithSignalR};
export default SignalRContext;
